import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { fetchRecipes } from "@/lib/api";
import { ArrowRight } from "lucide-react";

const CATEGORIES = [
  {
    slug: "sides",
    label: "Sides",
    tagline: "Small plates, big opening moves",
    number: "01",
  },
  {
    slug: "mains",
    label: "Main Course",
    tagline: "The centerpiece — the reason you sat down",
    number: "02",
  },
  {
    slug: "desserts",
    label: "Desserts",
    tagline: "The full-stop, but sweet",
    number: "03",
  },
];

export default function Home() {
  const [featured, setFeatured] = useState(null);

  useEffect(() => {
    // Try to fetch chicken adobo hero (with retry if seeding still in progress)
    const load = async (attempt = 0) => {
      try {
        const recipes = await fetchRecipes("mains");
        const adobo = recipes.find((r) => r.slug === "chicken-adobo") || recipes[0];
        if (adobo) setFeatured(adobo);
        else if (attempt < 4) setTimeout(() => load(attempt + 1), 600);
      } catch {
        if (attempt < 4) setTimeout(() => load(attempt + 1), 600);
      }
    };
    load();
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground grain" data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={
              featured?.hero_image ||
              "https://images.unsplash.com/photo-1688084448277-61229630522e?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
            }
            alt="Featured dish"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/30" />
        </div>

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 pb-24 pt-40 w-full">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-script text-white/90 text-2xl md:text-3xl mb-4"
            data-testid="hero-eyebrow"
          >
            a scroll-based cookbook
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-white text-5xl sm:text-7xl md:text-8xl lg:text-[9rem] leading-[0.9] tracking-tighter max-w-6xl"
            data-testid="hero-title"
          >
            Cook it slow.<br />
            <span className="italic text-primary">Read it slower.</span>
          </motion.h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-10 flex flex-col md:flex-row gap-6 md:items-end md:justify-between max-w-6xl"
          >
            <p className="font-serif-alt italic text-white/90 text-xl md:text-2xl max-w-xl leading-relaxed">
              Scroll through recipes like you&rsquo;d walk through a market — one basket, one step at a time. Set your servings, and every gram scales with you.
            </p>
            <Link
              to={featured ? `/recipe/${featured.slug}` : "/category/mains"}
              data-testid="hero-cta-featured"
              className="group inline-flex items-center gap-3 self-start px-8 py-4 bg-primary text-primary-foreground rounded-full font-serif-alt text-lg hover:bg-accent hover:text-accent-foreground transition-colors duration-500"
            >
              Start with Chicken Adobo
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" strokeWidth={1.5} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* SECTION DIVIDER */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 pt-32 pb-16">
        <div className="grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <p className="font-script text-accent text-3xl mb-4">the menu</p>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tighter">
              Three courses.<br />
              <span className="italic text-foreground/60">Ten recipes.</span><br />
              Zero shortcuts.
            </h2>
          </div>
          <div className="md:col-span-4">
            <p className="font-serif-alt text-lg italic text-foreground/70 leading-relaxed border-l-2 border-accent pl-6">
              &ldquo;The best recipes are patient with you. Ours are, too — they scale, they explain, and they never assume you&rsquo;ve done this before.&rdquo;
            </p>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 pb-32">
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {CATEGORIES.map((cat, i) => (
            <motion.div
              key={cat.slug}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
            >
              <CategoryCard {...cat} />
            </motion.div>
          ))}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-border py-16 px-6 md:px-12">
        <div className="max-w-[1600px] mx-auto flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <p className="font-display text-2xl tracking-tighter">Scrollinary<span className="text-accent">.</span></p>
          <p className="font-serif-alt italic text-sm text-foreground/60">Cook something worth remembering.</p>
        </div>
      </footer>
    </div>
  );
}

function CategoryCard({ slug, label, tagline, number }) {
  const [recipes, setRecipes] = useState([]);

  useEffect(() => {
    fetchRecipes(slug).then(setRecipes).catch(() => setRecipes([]));
  }, [slug]);

  const cover = recipes[0]?.hero_image;

  return (
    <Link
      to={`/category/${slug}`}
      data-testid={`category-card-${slug}`}
      className="group block relative overflow-hidden bg-secondary aspect-[3/4] rounded-sm"
    >
      {cover && (
        <img
          src={cover}
          alt={label}
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms] ease-out"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
      <div className="relative h-full p-8 flex flex-col justify-between text-white">
        <div className="flex justify-between items-start">
          <span className="font-display text-6xl md:text-7xl leading-none tracking-tighter opacity-90">{number}</span>
          <span className="font-serif-alt italic text-sm opacity-70">{recipes.length} recipes</span>
        </div>
        <div>
          <h3 className="font-display text-4xl md:text-5xl leading-none tracking-tighter mb-3">{label}</h3>
          <p className="font-serif-alt italic text-base text-white/80 max-w-xs">{tagline}</p>
          <div className="mt-6 inline-flex items-center gap-2 font-serif-alt text-sm border-b border-white/40 pb-1 group-hover:border-primary group-hover:text-primary transition-all duration-300">
            Browse the course
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" strokeWidth={1.5} />
          </div>
        </div>
      </div>
    </Link>
  );
}
